import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  extractHttpErrorDetail,
  MENSAJE_ERROR_TECNICO
} from 'src/app/common/http-error-utils';
import { MSG_CAMPOS_INVALIDOS } from 'src/app/common/mensajes-validacion';
import { redondearCantidad } from 'src/app/common/cantidad.utils';
import {
  CANAL,
  MAX_DIGITOS_EXPEDIENTE,
  MAX_DNI_BENEFICIARIO,
  MAX_NOMBRE_BENEFICIARIO,
  TIPO_EMPLEADO,
  TIPO_PLAN
} from 'src/app/common/constants-ejb';
import {
  CrearTareaRequest,
  PrepararIngresoCumplimientoRequest
} from 'src/app/domain/model/sin-oc/sin-oc';
import { Oficina } from 'src/app/domain/model/oficina/oficina';
import { Persona } from 'src/app/domain/model/persona/persona';
import { AseguradoraRepository } from 'src/app/domain/service/aseguradora/aseguradora-repository';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { OficinaRepository } from 'src/app/domain/service/oficina/oficina-repository';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import { HttpSucursalRepository } from 'src/app/infrastructure/adapters/sin-oc/http-sucursal.repository';

// Legacy WidgetsNamesConstants (CumplimientoSinOcViewImpl.validarCamposRequeridos/validarFechas):
// fechasInvalidas, cantidadInvalida, ErrorFormatoNumerico y seleccioneEstadoInvestigacion.
// fechasInvalidas/cantidadInvalida llevan el motivo para que el usuario sepa qué corregir;
// mismos textos que CrearTareaSinOcUseCase (backend).
export const MSG_FECHAS_INVALIDAS = 'Ingrese correctamente las fechas';
export const MSG_FECHA_INICIAL_MAYOR = `${MSG_FECHAS_INVALIDAS}: la fecha inicial no puede ser mayor que la final.`;
export const MSG_FECHAS_DISTINTO_MES = `${MSG_FECHAS_INVALIDAS}: deben pertenecer al mismo mes y año.`;
export const MSG_CANTIDAD_INVALIDA = 'Cantidad inválida: debe ser mayor a 0.';
export const MSG_EXPEDIENTE_FORMATO =
  'El campo numérico no tiene un formato válido - Campo: Expediente';
export const MSG_ESTADO_INVESTIGACION = 'Debe seleccionar el estado de investigación';

export interface CrearTareaSinOcValidationContext {
  form: Partial<CrearTareaRequest>;
  esTelefonico: boolean;
  mostrarExpediente: boolean;
  fechaEntregaInicial?: Date | null;
  fechaEntregaFinal?: Date | null;
}

export interface CrearTareaSinOcResetState {
  form: Partial<CrearTareaRequest>;
  fechaEntregaInicial: Date | null;
  fechaEntregaFinal: Date | null;
  esTelefonico: boolean;
  mostrarExpediente: boolean;
  mensajeError: string | null;
  nmconsecBeneficiarioLocal: number;
  nombreBeneficiarioDisplay: string;
}

@Injectable()
export class CrearTareaSinOcFacade {
  private readonly oficinaRepo = inject(OficinaRepository);
  private readonly catalogoRepo = inject(CatalogoRepository);
  private readonly aseguradoraRepo = inject(AseguradoraRepository);
  private readonly usuarioState = inject(UsuarioStateService);
  private readonly sucursalRepo = inject(HttpSucursalRepository);

  debeMostrarTipoLlamada(formaEntrega?: string): boolean {
    return formaEntrega === TIPO_PLAN.TELEFONICO;
  }

  puedeIngresarGestion(form: Partial<CrearTareaRequest>): boolean {
    return !!form.nmconsecOficina && !!form.cdcontratoBeneficiario?.trim();
  }

  ensureCanalServicioDefault(form: Partial<CrearTareaRequest>): void {
    if (!form.dscanalServicio || !String(form.dscanalServicio).trim()) {
      form.dscanalServicio = CANAL.CONSULTOR;
    }
  }

  validarCrearTarea(context: CrearTareaSinOcValidationContext): string | null {
    this.ensureCanalServicioDefault(context.form);
    const commonError = this.validarCamposComunes(context);
    if (commonError) {
      return commonError;
    }

    if (!context.fechaEntregaInicial || !context.fechaEntregaFinal) {
      return MSG_FECHAS_INVALIDAS;
    }

    const ini = context.fechaEntregaInicial;
    const fin = context.fechaEntregaFinal;

    if (ini > fin) {
      return MSG_FECHA_INICIAL_MAYOR;
    }

    if (
      ini.getFullYear() !== fin.getFullYear() ||
      ini.getMonth() !== fin.getMonth()
    ) {
      return MSG_FECHAS_DISTINTO_MES;
    }

    if (!context.form.cantidadTarea || context.form.cantidadTarea <= 0) {
      return MSG_CANTIDAD_INVALIDA;
    }

    return null;
  }

  validarIngresoCumplimiento(
    context: CrearTareaSinOcValidationContext
  ): string | null {
    this.ensureCanalServicioDefault(context.form);
    return this.validarCamposComunes(context);
  }

  formatFecha(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  async cargarOficinasYAplicarDefault(
    form: Partial<CrearTareaRequest>
  ): Promise<Oficina[]> {
    const oficinas = await this.oficinaRepo.obtenerOficinas();
    await this.aplicarOficinaDefaultEmpleado(form, oficinas);
    return oficinas;
  }

  /**
   * Modo línea de atención: al elegir la póliza se preselecciona la oficina cuyo
   * CDOFICINA (valor tras el ';' de lbOficinaOrden) es la CDDELEGACION de la póliza.
   * Si ninguna coincide la oficina queda sin seleccionar, como el legado
   * (setSelectedValue("")).
   * Legacy: CumplimientoSinOcViewImpl.seleccionaronItem() líneas 804-812.
   */
  aplicarOficinaPorDelegacion(
    form: Partial<CrearTareaRequest>,
    disponibles: Oficina[],
    cddelegacion: string | undefined
  ): void {
    const oficina = cddelegacion
      ? disponibles.find((o) => o.cdoficina === cddelegacion)
      : undefined;
    form.nmconsecOficina = oficina?.nmconsecutivo;
    form.cdoficina = oficina?.cdoficina;
  }

  /**
   * Modo línea de atención: tras elegir la póliza el legado busca las sucursales de la
   * oficina de la orden y toma automáticamente el primer registro
   * (BusquedaActivity.setObtenerPrimerRegistro(true)); sin resultados no selecciona nada.
   * Legacy: CumplimientoSinOcViewImpl.seleccionaronItem() líneas 813-816.
   */
  async obtenerPrimerCentroTrabajoLineaAtencion(
    npoliza: string | undefined,
    cdoficina: string | undefined
  ): Promise<{ cdcentroTrabajo: string; dscentroTrabajo: string } | null> {
    if (!npoliza?.trim() || !cdoficina?.trim()) {
      return null;
    }
    try {
      const response = await this.sucursalRepo.buscar(
        npoliza,
        undefined,
        undefined,
        null,
        1,
        'NEXT',
        cdoficina
      );
      const primera = response?.content?.[0];
      return primera
        ? {
            cdcentroTrabajo: primera.cdsucursal ?? '',
            dscentroTrabajo: primera.dssucursal ?? ''
          }
        : null;
    } catch (error) {
      console.error('[CrearTareaSinOC] Error buscando la primera sucursal:', error);
      return null;
    }
  }

  async aplicarOficinaDefaultEmpleado(
    form: Partial<CrearTareaRequest>,
    disponibles: Oficina[]
  ): Promise<void> {
    if (form.nmconsecOficina) {
      return;
    }
    try {
      let usuario = await this.usuarioState.cargar();
      if (usuario && usuario.nmconsecOficina == null) {
        usuario = await this.usuarioState.recargar();
      }
      const nmconsec = usuario?.nmconsecOficina;
      if (nmconsec == null) {
        console.warn(
          '[CrearTareaSinOC] Usuario en sesión sin nmconsecOficina, nmconsecutivo:',
          usuario?.nmconsecutivo
        );
        return;
      }
      const existe = disponibles.some((o) => o.nmconsecutivo === nmconsec);
      if (existe) {
        form.nmconsecOficina = nmconsec;
        form.cdoficina = disponibles.find(
          (o) => o.nmconsecutivo === nmconsec
        )?.cdoficina;
      } else {
        console.warn(
          '[CrearTareaSinOC] Oficina del empleado no está en el catálogo activo',
          {
            nmconsecOficinaEmpleado: nmconsec,
            cdoficina: usuario?.cdoficina,
            oficinasDisponibles: disponibles.length
          }
        );
      }
    } catch (error) {
      console.warn(
        '[CrearTareaSinOC] No se pudo resolver oficina del usuario',
        error
      );
    }
  }

  async cargarTareasValidasExpediente(): Promise<string[]> {
    try {
      return await this.catalogoRepo.getTareasValidasExpediente();
    } catch {
      return [];
    }
  }

  async cargarAseguradoraDefault(
    form: Partial<CrearTareaRequest>
  ): Promise<string> {
    try {
      const aseguradora =
        await this.aseguradoraRepo.obtenerAseguradoraDefault();
      if (!aseguradora) return '';
      form.nmconsecCliente = aseguradora.nmconsecutivo;
      return aseguradora.nombreComercial || '';
    } catch {
      return '';
    }
  }

  async initBuscadorAsesor(form: Partial<CrearTareaRequest>): Promise<{
    asesorSeleccionado: Persona | null;
    asesorBloqueado: boolean;
  }> {
    try {
      const usuario = await this.usuarioState.cargar();
      if (!usuario || usuario.cdtipoEmpleado !== TIPO_EMPLEADO.ASESOR) {
        return { asesorSeleccionado: null, asesorBloqueado: false };
      }

      form.cdpersonaAsesor = usuario.cdpersona;
      return {
        asesorSeleccionado: {
          cdpersona: usuario.cdpersona,
          dsnombre1: usuario.primerNombre,
          dsnombre2: usuario.segundoNombre,
          dsapellido1: usuario.primerApellido,
          dsapellido2: usuario.segundoApellido
        } as Persona,
        asesorBloqueado: !usuario.pymes
      };
    } catch (error) {
      console.warn(
        '[CrearTareaSinOC] No se pudo resolver initBuscadorAsesor',
        error
      );
      return { asesorSeleccionado: null, asesorBloqueado: false };
    }
  }

  requiereExpediente(
    cdservicio: string,
    tareasValidasExpediente: string[]
  ): boolean {
    return tareasValidasExpediente.includes(cdservicio);
  }

  construirRequestCrearTarea(
    form: Partial<CrearTareaRequest>,
    fechaEntregaInicial: Date,
    fechaEntregaFinal: Date
  ): CrearTareaRequest {
    const requestBase = this.extraerCamposObligatorios(form);

    return {
      cdpersonaAsesor: requestBase.cdpersonaAsesor,
      nmconsecCliente: requestBase.nmconsecCliente,
      nmconsecOficina: requestBase.nmconsecOficina,
      cdoficina: requestBase.cdoficina,
      dniBeneficiario: requestBase.dniBeneficiario,
      dsnombreBeneficiario: requestBase.dsnombreBeneficiario,
      cdpersonaBeneficiario: requestBase.cdpersonaBeneficiario,
      cdcontratoBeneficiario: requestBase.cdcontratoBeneficiario,
      cdcentroTrabajo: requestBase.cdcentroTrabajo,
      dscentroTrabajo: requestBase.dscentroTrabajo,
      nmconsecHomologacion: requestBase.nmconsecHomologacion,
      nmconsecServicioCliente: form.nmconsecServicioCliente ?? 0,
      cdservicio: requestBase.cdservicio,
      dsclasificacion: requestBase.dsclasificacion,
      cdproducto: requestBase.cdproducto.trim(),
      dsproducto: requestBase.dsproducto.trim(),
      dscanalServicio: requestBase.dscanalServicio,
      formaEntrega: requestBase.formaEntrega,
      tipoLlamada: form.tipoLlamada ?? '',
      cdmunicipioOrigen: requestBase.cdmunicipioOrigen,
      cdmunicipioEntrega: requestBase.cdmunicipioEntrega,
      fechaEntregaInicial: this.formatFecha(fechaEntregaInicial),
      fechaEntregaFinal: this.formatFecha(fechaEntregaFinal),
      // Solo Crear Tarea exige la cantidad; Ingresar Cumplimiento no la captura.
      cantidadTarea: redondearCantidad(
        this.requireNumber(form.cantidadTarea, 'cantidadTarea')
      ),
      nmexpediente: form.nmexpediente,
      dsestadoInvestigacion: form.dsestadoInvestigacion
    };
  }

  construirRequestPrepararIngresoCumplimiento(
    form: Partial<CrearTareaRequest>
  ): PrepararIngresoCumplimientoRequest {
    const requestBase = this.extraerCamposObligatorios(form);

    return {
      cdpersonaAsesor: requestBase.cdpersonaAsesor,
      nmconsecCliente: requestBase.nmconsecCliente,
      nmconsecOficina: requestBase.nmconsecOficina,
      cdoficina: requestBase.cdoficina,
      dniBeneficiario: requestBase.dniBeneficiario,
      dsnombreBeneficiario: requestBase.dsnombreBeneficiario,
      cdpersonaBeneficiario: requestBase.cdpersonaBeneficiario,
      cdcontratoBeneficiario: requestBase.cdcontratoBeneficiario,
      cdcentroTrabajo: requestBase.cdcentroTrabajo,
      dscentroTrabajo: requestBase.dscentroTrabajo,
      nmconsecHomologacion: requestBase.nmconsecHomologacion,
      nmconsecServicioCliente: form.nmconsecServicioCliente ?? 0,
      cdservicio: requestBase.cdservicio,
      dsclasificacion: requestBase.dsclasificacion,
      cdproducto: requestBase.cdproducto.trim(),
      dsproducto: requestBase.dsproducto.trim(),
      dscanalServicio: requestBase.dscanalServicio,
      formaEntrega: requestBase.formaEntrega,
      tipoLlamada: form.tipoLlamada ?? '',
      cdmunicipioOrigen: requestBase.cdmunicipioOrigen,
      cdmunicipioEntrega: requestBase.cdmunicipioEntrega,
      nmexpediente: form.nmexpediente,
      dsestadoInvestigacion: form.dsestadoInvestigacion
    };
  }

  /**
   * Legacy CumplimientoSinOcActivity: muestra el mensaje del negocio/SP tal cual y,
   * ante fallo técnico (handleErrors), genericError.
   */
  extraerMensajeError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      // Status 0: el interceptor ya dejó en `message` el texto genérico de comunicación
      if (err.status === 0) {
        return err.message || MENSAJE_ERROR_TECNICO;
      }
      // 5xx: el detail es técnico ("Error técnico interno"); el legacy mostraba genericError
      if (err.status >= 500) {
        return MENSAJE_ERROR_TECNICO;
      }
      return extractHttpErrorDetail(err) ?? MENSAJE_ERROR_TECNICO;
    }
    // Error propio del armado del request (requireNumber/requireString): es un bug del
    // formulario, no algo que el usuario pueda corregir; no se muestra el texto técnico.
    console.warn('[CrearTareaSinOC] Error armando la solicitud', err);
    return MENSAJE_ERROR_TECNICO;
  }

  resetFormularioState(): CrearTareaSinOcResetState {
    return {
      form: { dscanalServicio: CANAL.CONSULTOR },
      fechaEntregaInicial: null,
      fechaEntregaFinal: null,
      esTelefonico: false,
      mostrarExpediente: false,
      mensajeError: null,
      nmconsecBeneficiarioLocal: 0,
      nombreBeneficiarioDisplay: ''
    };
  }

  private validarCamposComunes(
    context: CrearTareaSinOcValidationContext
  ): string | null {
    const form = context.form;

    // Legacy validarCamposRequeridos: validateRequiredFields acumula TODOS los campos vacíos
    // bajo requiredFieldsNoData, uno por renglón. Se nombran con la etiqueta de esta pantalla.
    const requeridos: Array<{ faltante: boolean; campo: string }> = [
      { faltante: !form.nmconsecOficina, campo: 'Oficina de Atención' },
      { faltante: !form.nmconsecCliente, campo: 'Aseguradora' },
      // Legacy: el beneficiario se toma de la póliza (DNI + nombre comercial); no exige CDPERSONA
      { faltante: !form.dniBeneficiario?.trim(), campo: 'Beneficiario / Cliente' },
      { faltante: !form.cdservicio, campo: 'Tarea / Servicio' },
      { faltante: !form.cdproducto?.trim(), campo: 'Producto' },
      { faltante: !form.cdcentroTrabajo, campo: 'Centro de Trabajo' },
      { faltante: !form.cdpersonaAsesor, campo: 'Consultor' },
      { faltante: !form.dscanalServicio, campo: 'Canal de Servicio' },
      { faltante: !form.formaEntrega, campo: 'Forma de Entrega' },
      { faltante: context.esTelefonico && !form.tipoLlamada, campo: 'Tipo de Llamada' },
      { faltante: !form.cdmunicipioOrigen, campo: 'Municipio Origen' },
      { faltante: !form.cdmunicipioEntrega, campo: 'Municipio Entrega' }
    ];

    const lineas: string[] = [];
    const faltantes = requeridos.filter((r) => r.faltante).map((r) => r.campo);
    if (faltantes.length > 0) {
      lineas.push(MSG_CAMPOS_INVALIDOS, ...faltantes);
    }

    // Límites de longitud (seguridad, sin equivalente legacy); iguales a CrearTareaRequest
    if ((form.dniBeneficiario?.trim().length ?? 0) > MAX_DNI_BENEFICIARIO) {
      lineas.push(`El DNI del Beneficiario no puede superar ${MAX_DNI_BENEFICIARIO} caracteres.`);
    }
    if ((form.dsnombreBeneficiario?.trim().length ?? 0) > MAX_NOMBRE_BENEFICIARIO) {
      lineas.push(`El nombre del Beneficiario no puede superar ${MAX_NOMBRE_BENEFICIARIO} caracteres.`);
    }

    // Legacy: el expediente vacío, en cero o mal formado da ErrorFormatoNumerico, y el estado
    // de investigación se agrega en su propio renglón.
    if (context.mostrarExpediente) {
      const expVal = String(form.nmexpediente ?? '').trim();
      const expedienteValido =
        /^\d+$/.test(expVal) &&
        !/^0+$/.test(expVal) &&
        expVal.length <= MAX_DIGITOS_EXPEDIENTE;
      if (!expedienteValido) {
        lineas.push(MSG_EXPEDIENTE_FORMATO);
      }
      if (!form.dsestadoInvestigacion) {
        lineas.push(MSG_ESTADO_INVESTIGACION);
      }
    }

    return lineas.length > 0 ? lineas.join('\n') : null;
  }

  private extraerCamposObligatorios(form: Partial<CrearTareaRequest>): {
    cdpersonaAsesor: number;
    nmconsecCliente: number;
    nmconsecOficina: number;
    cdoficina: string;
    dniBeneficiario: string;
    dsnombreBeneficiario: string;
    cdpersonaBeneficiario: number | undefined;
    cdcontratoBeneficiario: string;
    cdcentroTrabajo: string;
    dscentroTrabajo: string;
    nmconsecHomologacion: number;
    cdservicio: string;
    dsclasificacion: string;
    cdproducto: string;
    dsproducto: string;
    dscanalServicio: string;
    formaEntrega: string;
    cdmunicipioOrigen: string;
    cdmunicipioEntrega: string;
  } {
    return {
      cdpersonaAsesor: this.requireNumber(
        form.cdpersonaAsesor,
        'cdpersonaAsesor'
      ),
      nmconsecCliente: this.requireNumber(
        form.nmconsecCliente,
        'nmconsecCliente'
      ),
      nmconsecOficina: this.requireNumber(
        form.nmconsecOficina,
        'nmconsecOficina'
      ),
      cdoficina: this.requireString(form.cdoficina, 'cdoficina'),
      dniBeneficiario: this.requireString(
        form.dniBeneficiario,
        'dniBeneficiario'
      ).trim(),
      dsnombreBeneficiario: this.requireString(
        form.dsnombreBeneficiario,
        'dsnombreBeneficiario'
      ).trim(),
      // Una póliza sin persona en TPSC_PERSONAS llega con 0/null: no se envía
      cdpersonaBeneficiario: form.cdpersonaBeneficiario || undefined,
      cdcontratoBeneficiario: this.requireString(
        form.cdcontratoBeneficiario,
        'cdcontratoBeneficiario'
      ),
      cdcentroTrabajo: this.requireString(
        form.cdcentroTrabajo,
        'cdcentroTrabajo'
      ),
      dscentroTrabajo: this.requireString(
        form.dscentroTrabajo,
        'dscentroTrabajo'
      ),
      nmconsecHomologacion: this.requireNumber(
        form.nmconsecHomologacion,
        'nmconsecHomologacion'
      ),
      cdservicio: this.requireString(form.cdservicio, 'cdservicio'),
      dsclasificacion: this.requireString(
        form.dsclasificacion,
        'dsclasificacion'
      ),
      cdproducto: this.requireString(form.cdproducto, 'cdproducto'),
      dsproducto: this.requireString(form.dsproducto, 'dsproducto'),
      dscanalServicio: this.requireString(
        form.dscanalServicio,
        'dscanalServicio'
      ),
      formaEntrega: this.requireString(form.formaEntrega, 'formaEntrega'),
      cdmunicipioOrigen: this.requireString(
        form.cdmunicipioOrigen,
        'cdmunicipioOrigen'
      ),
      cdmunicipioEntrega: this.requireString(
        form.cdmunicipioEntrega,
        'cdmunicipioEntrega'
      )
    };
  }

  private requireNumber(value: number | undefined, fieldName: string): number {
    if (value == null) {
      throw new Error(
        `[CrearTareaSinOcFacade] Campo requerido faltante: ${fieldName}`
      );
    }
    return value;
  }

  private requireString(value: string | undefined, fieldName: string): string {
    if (!value || !value.trim()) {
      throw new Error(
        `[CrearTareaSinOcFacade] Campo requerido faltante: ${fieldName}`
      );
    }
    return value;
  }
}
